import React, { useState } from 'react';
import AdminHeader from '../../components/navigation/AdminHeader';
import DashboardHeader from './components/DashboardHeader';
import CompanyMetrics from './components/CompanyMetrics';
import CompanyFilters from './components/CompanyFilters';
import CompanyTable from './components/CompanyTable';
import CompanyTableMobile from './components/CompanyTableMobile';
import LoadingState from './components/LoadingState';
import ErrorAlert from './components/ErrorAlert';
import TableFooter from './components/TableFooter';
import { useCompanyManagement } from './hooks/useCompanyManagement';
import { filterCompanies, calculateMetrics, sortCompanies } from './utils/companyUtils';

/**
 * Main admin dashboard component for managing companies
 * Refactored into smaller, focused components for better maintainability
 */
const AdminDashboard = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const {
    companies,
    loading,
    error,
    handleToggleStatus,
    handleDeleteCompany
  } = useCompanyManagement();

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  // Apply filtering and sorting
  const filteredCompanies = filterCompanies(companies, searchQuery, statusFilter);
  const sortedCompanies = sortCompanies(filteredCompanies, sortConfig);
  const metrics = calculateMetrics(companies);

  if (loading) {
    return <LoadingState />;
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminHeader />
      <div className="main-content">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <DashboardHeader />
          <ErrorAlert message={error} />
          <CompanyMetrics metrics={metrics} />

          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <CompanyFilters
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
            />

            <CompanyTable
              companies={sortedCompanies}
              sortConfig={sortConfig}
              onSort={handleSort}
              onToggleStatus={handleToggleStatus}
              onDelete={handleDeleteCompany}
            />

            <div className="lg:hidden">
              {sortedCompanies?.length > 0 ? (
                sortedCompanies?.map((company) => (
                  <CompanyTableMobile
                    key={company?.id}
                    company={company}
                    onToggleStatus={handleToggleStatus}
                    onDelete={handleDeleteCompany}
                  />
                ))
              ) : (
                <div className="bg-white rounded-xl p-8 text-center text-text-secondary">
                  No companies found
                </div>
              )}
            </div>
          </div>

          <TableFooter 
            filteredCount={sortedCompanies?.length} 
            totalCount={metrics?.total} 
          />
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;